import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@companycam/slab-web';

import toast from 'helpers/toast';

/**
 * @typedef {Object} InputClipboardProps
 * @property {() => void} [asyncErrorAction] - Action to handle async errors.
 * @property {string} [asyncErrorMessage] - Error message for async scenarios.
 * @property {boolean} [isLoading] - Flag indicating async loading state.
 * @property {string | null} text - Text to be copied.
 * @property {() => void} [onCopySuccess] - Callback for successful copy.
 * @property {string} [`data-testid`] - Id for testing.
 * @property {string} [accessibilitylabel] - Accessibility label for non-readable text.
 * @property {() => void} [onTextSelected] - Callback for text selection.
 */

/**
 *
 * @param {InputClipboardProps} props - Component props.
 * @returns {JSX.Element} React component.
 *
 * @example
 * // Async Scenario:
 * // Assume asyncErrorAction is a function to handle async errors,
 * // asyncErrorMessage is an error message, isLoading is a loading state flag,
 * // text is fetched asynchronously, and onCopySuccess is a callback for successful copy.
 * <InputClipboard
 *   asyncErrorAction={handleAsyncError}
 *   asyncErrorMessage="Failed to fetch text."
 *   isLoading={isLoading}
 *   text={asyncFetchedText}
 *   onCopySuccess={handleCopySuccess}
 *   data-testid="trusty_clipboard"
 *   accessibilityLabel="Invitation link"
 *   onTextSelected={handleTextSelected}
 * />;
 *
 * @example
 * // Sync Scenario:
 * // If the text is readily available without any async operations.
 * <InputClipboard text="This is a sync text." onCopySuccess={handleCopySuccess} />;
 */

type InputClipboardProps = {
  asyncErrorAction?: () => void;
  asyncErrorMessage?: string;
  isLoading?: boolean;
  text: string | null;
  onCopySuccess?: () => void;
  'data-testid'?: string;
  accessibilityLabel?: string;
  onTextSelected?: () => void;
  'input-data-testid'?: string;
};

type TextInputProps = {
  hasAsyncError: boolean;
};

export const messages = defineMessages({
  copyError: {
    id: 'InputClipboard.copyError',
    defaultMessage: 'Could not copy this text.',
    description: 'Error message if clipboard API fails to copy text',
  },
  copySuccess: {
    id: 'InputClipboard.copySuccess',
    defaultMessage: 'Link Copied to Clipboard',
    description: 'Success text if link is copied to clipboard',
  },
  copy: {
    id: 'InputClipboard.copy',
    defaultMessage: 'Copy',
    description:
      'Message for copying trusty links (invite, payment requests, etc)',
  },
  retry: {
    id: 'InputClipboard.retry',
    defaultMessage: 'Retry',
    description:
      'Message to retry fetch requests for trusty links (invite, payment requests, etc)',
  },
});

// #region styled-components

const InputContainer = styled.div<TextInputProps>`
  display: flex;
  border-radius: var(--cc_size_border_radius_l);
  align-items: center;
  background-color: ${(props) =>
    props.hasAsyncError
      ? 'var(--cc_color_utility_destroy_lighten_95)'
      : 'var(--cc_color_background_2)'};
  padding: 0 var(--cc_size_spacing_xs) 0
    calc(var(--cc_size_spacing_s) + var(--cc_size_spacing_xs));
  flex-wrap: wrap;
  flex: 1;
`;

const Container = styled.div`
  display: flex;
  gap: var(--cc_size_spacing_s);
`;

const TextInput = styled.input<TextInputProps>`
  && {
    display: flex;
    width: unset;
    flex: 1;
    font-size: var(--cc_size_text_s);
    background-color: transparent;
    border: 0;
    padding: 0;
    border-radius: var(--cc_size_border_radius_m);
    color: ${(props) =>
      props.hasAsyncError
        ? 'var(--cc_color_utility_destroy)'
        : `var(--cc_color_text_subtle)`};
  }
`;

// #endregion

const InputClipboard = ({
  asyncErrorAction,
  asyncErrorMessage,
  text,
  isLoading,
  onCopySuccess,
  accessibilityLabel,
  onTextSelected,
  ...props
}: InputClipboardProps) => {
  const { formatMessage } = useIntl();
  const hasAsyncError = !!asyncErrorMessage;

  const handleOnClick = async (
    e:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    if (hasAsyncError) {
      asyncErrorAction();
    } else {
      try {
        await navigator.clipboard.writeText(text);
        toast.success(formatMessage(messages.copySuccess));
        onCopySuccess?.();
      } catch (error) {
        toast.error(formatMessage(messages.copyError));
      }
    }
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    onTextSelected?.();
    e.target.select();
  };

  return (
    <Container>
      <InputContainer
        onClick={(e) => e.stopPropagation()}
        hasAsyncError={hasAsyncError}
      >
        <TextInput
          aria-label={accessibilityLabel}
          hasAsyncError={hasAsyncError}
          value={hasAsyncError ? asyncErrorMessage : text}
          readOnly
          onFocus={onFocus}
          data-testid={props['input-data-testid']}
        />
      </InputContainer>
      <Button
        data-testid={props['data-testid']}
        design="outline"
        onClick={handleOnClick}
        color="subtle"
        loading={isLoading}
      >
        {hasAsyncError
          ? formatMessage(messages.retry)
          : formatMessage(messages.copy)}
      </Button>
    </Container>
  );
};

export default InputClipboard;
